<template>
	<div class="email-wrapper">
		<div
			class="spinner-container"
			v-if="isLoadingSession"
		>
			<TnSpinner
				size="l"
				color="lightblue"
			/>
		</div>
		<div
			class="login-logout-container"
			v-else
		>
			<div
				class="email-login"
				v-if="!signedIn"
			>
				<h1>{{ titles?.componentTitle || defaults.titles.componentTitle }}</h1>
				<TnButton
					icon-left="telenor"
					:primary="true"
					:dark="true"
					class="margin-top-3xl"
					id="telenor-id-btn"
					@click="telenorIdSignIn('/online/epost')"
				>
					{{ buttonsAndLinks?.loginWithTelenorIdText || defaults.buttonsAndLinks.loginWithTelenorIdText }}
				</TnButton>
				<div class="separator margin-top-3xl">
					<span>{{ separators?.separatorText || defaults.separators.separatorText }}</span>
				</div>
				<form
					id="loginemailform"
					class="login-email-form"
					@submit.prevent.stop="handleFormLogin"
				>
					<input
						type="hidden"
						name="CustomError"
						:value="customErrorUrl"
					/>
					<input
						type="hidden"
						name="CustomLogin"
						:value="EpostConstants.EPOST.LOGIN_URL"
					/>
					<TnTextField
						v-model="username"
						:dark="true"
						size="m"
						type="email"
						label="E-postadresse:"
						name="user"
						placeholder="dinepost@online.no"
						id="email-username"
						class="margin-top-xl"
						:clearable="true"
						required
						aria-label="E-postadresse"
					/>
					<TnTextField
						v-model="password"
						:dark="true"
						size="m"
						type="password"
						label="Passord:"
						name="password"
						placeholder="Ditt online passord"
						id="email-password"
						class="margin-top-m"
						:clearable="true"
						required
						aria-label="E-post passord"
					/>
					<div v-if="loginInfoBox?.showInfoBox">
						<TnNotification
							class="margin-top-m"
							type="info"
							:title="loginInfoBox?.loginInfoBoxTitle || defaults.loginInfoBox.loginInfoBoxTitle"
						>
							<span>{{ loginInfoBox?.loginInfoBoxIngress || defaults.loginInfoBox.loginInfoBoxIngress }}</span>
							<TnLink
								:to="loginInfoBox?.loginInfoBoxUrl || defaults.loginInfoBox.loginInfoBoxUrl"
								:target="loginInfoBox?.openInfoBoxInSameTab ? '_self' : '_blank'"
								block
							>
								{{ loginInfoBox?.loginInfoBoxUrlText || defaults.loginInfoBox.loginInfoBoxUrlText }}
							</TnLink>
						</TnNotification>
					</div>
					<div class="margin-top-l bottom-cta-group">
						<TnButton
							class="login-submit-button"
							:primary="true"
							:dark="true"
							:disabled="isSubmitting"
							type="submit"
						>
							<span v-if="!isSubmitting">
								{{ buttonsAndLinks?.loginButtonText || defaults.buttonsAndLinks.loginButtonText }}
							</span>
							<span v-else>
								{{ buttonsAndLinks?.loggingInButtonText || defaults.buttonsAndLinks.loggingInButtonText }}
								<TnSpinner
									size="s"
									color="lightblue"
								/>
							</span>
						</TnButton>
						<TnLink
							class="margin-top-s"
							:dark="true"
							:text="buttonsAndLinks?.forgotPasswordText || defaults.buttonsAndLinks.forgotPasswordText"
							:to="buttonsAndLinks?.forgotPasswordUrl || defaults.buttonsAndLinks.forgotPasswordUrl"
						/>
					</div>
					<TnNotification
						v-if="error"
						type="error"
						role="alert"
						aria-live="assertive"
						class="margin-bottom-m"
					>
						{{ error }}
					</TnNotification>
				</form>
			</div>
			<div
				class="email-logout"
				v-else
			>
				<h1>{{ titles?.loggedInTitle || defaults.titles.loggedInTitle }}</h1>
				<TnParagraph
					class="margin-top-m"
					:dark="true"
					size="s"
				>
					{{ titles?.loggedInDescription || defaults.titles.loggedInDescription }}
				</TnParagraph>
				<TnButton
					:primary="true"
					:dark="true"
					class="margin-top-3xl"
					@click="goToEpost"
				>
					{{ buttonsAndLinks?.goToEpostText || defaults.buttonsAndLinks.goToEpostText }}
				</TnButton>
				<div class="separator margin-top-3xl">
					<span>{{ separators?.separatorTextWhenLoggedIn || defaults.separators.separatorTextWhenLoggedIn }}</span>
				</div>
				<TnButton
					:secondary="true"
					:dark="true"
					class="margin-top-3xl"
					@click="() => telenorIdSignOut('/online')"
				>
					{{ buttonsAndLinks?.logoutText || defaults.buttonsAndLinks.logoutText }}
				</TnButton>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { Schema } from "./epost-login.js";
import EpostConstants from "../common/epostConstants";
import mgr from "~/telenorid/telenorid";
import defaults from "./epostLoginDefaults";
import { useNotificationStore } from "~/pinia/platform/notifications/notifications";

const props = defineProps<Schema>();
const runtimeConfig = useRuntimeConfig();
const notification = useNotificationStore();

const isLoadingSession = ref(true);
const isSubmitting = ref(false);
const username = defineModel<string>("username", { default: "" });
const password = defineModel<string>("password", { default: "" });

const error = ref<string>("");
const showError = (msg: string) => {
	error.value = msg;
	nextTick(() => {
		const alert = document.querySelector('[role="alert"]');
		if (alert) (alert as HTMLElement).focus();
	});
};
const customErrorUrl = computed(() => window.location.origin + window.location.pathname + "?loginError=1");
const customLoginUrl = computed(() => window.location.origin + window.location.pathname);

const { titles, buttonsAndLinks, separators, loginInfoBox, errors } = props.content || {};

const router = useRouter();
const goToEpost = () => {
	const currentPath = router.currentRoute.value.path.replace(/\/$/, "");
	router.push(`${currentPath}/epost`);
};
const signedIn = ref(false);

const initializeSession = async () => {
	try {
		const user = await mgr.getOrLoginUser();
		signedIn.value = !!user;
	} catch (err) {
		console.error("Error during session initialization:", err);
		signedIn.value = false;
	} finally {
		isLoadingSession.value = false;
	}
};

onMounted(async () => {
	await initializeSession();

	const params = new URLSearchParams(window.location.search);
	if (params.get("loginError") === "1") {
		showError(errors?.errorInvalidCredentials || defaults.errors.errorInvalidCredentials);
	}
});

// Bypass form interceptor. Was solved by a proxy service earlier in BFF, but we do not want this traffic to hit our servers directly.
const handleFormLogin = (event: SubmitEvent) => {
	event.preventDefault();
	showError("");
	isSubmitting.value = true;

	try {
		submitFormToHostopia();
	} catch (err) {
		console.error("Form submission failed:", err);
		showError("Noe gikk galt under innsending. Prøv igjen.");
		isSubmitting.value = false;
	}
};

const addHiddenField = (form: HTMLFormElement, name: string, value: string) => {
	const input = document.createElement("input");
	input.type = "hidden";
	input.name = name;
	input.value = value;
	form.appendChild(input);
};

// Submits a native form to Hostopia, using hidden inputs.
const submitFormToHostopia = () => {
	const form = document.createElement("form");
	form.method = "POST";
	form.action = runtimeConfig.public.hostopia.formLoginUrl;

	addHiddenField(form, "user", username.value);
	addHiddenField(form, "password", password.value);
	addHiddenField(form, "CustomError", customErrorUrl.value);
	addHiddenField(form, "CustomLogin", customLoginUrl.value);

	document.body.appendChild(form);
	form.submit();
};

const telenorIdSignIn = async (callbackUrl: string) => {
	try {
		await mgr.loginRedirectBackHere({ from: "online" }, false, callbackUrl);
	} catch (err) {
		console.error("Error during Telenor ID login:", err);
		notification.error({
			title: "Innlogging feilet",
			message: errors?.telenorIdLoginError || defaults.errors.telenorIdLoginError,
		});
	}
};

const telenorIdSignOut = async (callbackUrl: string) => {
	try {
		await mgr.setCallbackRedirectUrl(callbackUrl);
		await mgr.signoutRedirect();
	} catch (err) {
		console.error("Error during Telenor ID logout:", err);
		notification.error({
			title: "Utlogging feilet",
			message: errors?.telenorIdLogoutError || defaults.errors.telenorIdLogoutError,
		});
	}
};
</script>

<style lang="scss" scoped>
::v-deep(#telenor-id-btn svg g#Telenor path) {
	fill: inherit !important;
}

.separator {
	display: flex;
	align-items: center;
	text-align: center;

	&::before,
	&::after {
		content: "";
		flex: 1;
		height: 1px;
		background-color: #c5cbdd;
	}

	span {
		margin: 0 48px;
		font-size: 16px;
	}
}

.email-wrapper {
	width: 100%;
	padding: 70px 32px;
	background: var(--Surface-Inverted, #000b2e);
	color: #fff;
	display: flex;
	justify-content: center;
	align-items: center;
}

.spinner-container {
	display: flex;
	justify-content: center;
	align-items: center;
	min-height: 460px;
	width: 100%;
}

h1 {
	@include font-title-s;
}

.login-logout-container {
	display: flex;
	flex-direction: column;
	flex: 1;
}
.email-login,
.email-logout {
	width: 100%;
	max-width: 500px;
	display: flex;
	flex-direction: column;
}

.email-logout {
	padding-top: 46px;
}

.email-login {
	.login-email-form {
		display: flex;
		flex-direction: column;
	}

	.bottom-cta-group {
		display: flex;
		flex-direction: column;
	}
}

.login-submit-button span {
	display: flex;
	align-items: center;
}

.login-submit-button .spinner {
	margin-left: 8px;
}
</style>
